import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../../hooks/useAuth';
import { getPurchases, cancelOrder, getOrder } from '../../../services/orderService';
import OrderCard from './OrderCard';
import OrderDetailsModal from './OrderDetailsModal';
import { Button } from '../../ui';
import { db } from '../../../firebase/config';
import { collection, query, where, orderBy, limit, onSnapshot } from 'firebase/firestore';

/**
 * PurchasesTab - Tab de servicios contratados (como comprador)
 */
const PurchasesTab = () => {
  const { currentUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [liveLastVisible, setLiveLastVisible] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [filter, setFilter] = useState('all'); // all | pending | in_progress | completed
  const [subKey, setSubKey] = useState(0);

  const LIVE_LIMIT = 50;
  const liveOrdersRef = useRef([]);
  const olderOrdersRef = useRef([]);
  const selectedOrderRef = useRef(null);

  useEffect(() => {
    selectedOrderRef.current = selectedOrder;
  }, [selectedOrder]);

  const mergeOrders = useCallback((live, older) => {
    const liveIds = new Set((live || []).map((o) => o.id));
    const tail = (older || []).filter((o) => !liveIds.has(o.id));
    return [...(live || []), ...tail];
  }, []);

  const loadOrders = useCallback(() => {
    if (!currentUser) return;
    setLoading(true);
    setError(null);
    olderOrdersRef.current = [];
    liveOrdersRef.current = [];
    setOrders([]);
    setLastVisible(null);
    setLiveLastVisible(null);
    setHasMore(false);
    setSubKey((k) => k + 1);
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadOrders();
    }
  }, [currentUser, loadOrders]);

  // Suscripción en tiempo real para las órdenes más recientes (para badges/estado/chat en vivo)
  useEffect(() => {
    if (!currentUser?.uid) return;

    const ordersRef = collection(db, 'ServiceOrders');
    const q = query(
      ordersRef,
      where('clientId', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(LIVE_LIMIT)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const live = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
        liveOrdersRef.current = live;
        setLiveLastVisible(snapshot.docs[snapshot.docs.length - 1] || null);
        setHasMore(snapshot.docs.length === LIVE_LIMIT || hasMore);
        setOrders(mergeOrders(live, olderOrdersRef.current));

        const sel = selectedOrderRef.current;
        if (sel?.id) {
          const updatedSel = live.find((o) => o.id === sel.id);
          if (updatedSel) setSelectedOrder(updatedSel);
        }

        setError(null);
        setLoading(false);
      },
      (err) => {
        setError(err?.message || 'Error al escuchar compras');
        setLoading(false);
      }
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.uid, subKey, mergeOrders]);

  const loadMore = async () => {
    if (!hasMore || loading || !currentUser) return;

    setLoading(true);
    const cursor = lastVisible || liveLastVisible;
    const result = await getPurchases(currentUser.uid, 20, cursor);

    if (result.success) {
      olderOrdersRef.current = [...(olderOrdersRef.current || []), ...(result.orders || [])];
      setOrders(mergeOrders(liveOrdersRef.current, olderOrdersRef.current));
      setLastVisible(result.lastVisible);
      setHasMore(result.hasMore);
    }

    setLoading(false);
  };

  const handleCancel = async (order) => {
    if (!window.confirm('¿Estás seguro de que quieres cancelar esta orden?')) {
      return;
    }

    const result = await cancelOrder(order.id, 'Cancelado por el cliente');

    if (result.success) {
      // Actualizar la orden localmente
      setOrders(prev => prev.map(o => 
        o.id === order.id ? { ...o, status: 'cancelled' } : o
      ));
      alert('Orden cancelada exitosamente');
    } else {
      alert('Error al cancelar la orden: ' + result.error);
    }
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : orders.filter(o => o.status === filter);

  // Loading state
  if (loading && orders.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
        <p className="text-gray-600 dark:text-gray-400">Cargando tus compras...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="text-center py-16">
        <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-red-100 to-orange-100 dark:from-red-900/30 dark:to-orange-900/30 rounded-full flex items-center justify-center">
          <svg className="w-12 h-12 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Error al cargar compras
        </h3>
        <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
        <Button variant="outline" onClick={loadOrders}>
          Reintentar
        </Button>
      </div>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <div className="text-center py-16">
        <div className="max-w-md mx-auto">
          <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            No has contratado servicios aún
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Explora servicios de otros creadores y solicita el que necesites
          </p>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Filtros */}
      <div className="mb-6 flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          Todos ({orders.length})
        </button>
        <button
          onClick={() => setFilter('pending')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'pending'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          Pendientes ({orders.filter(o => o.status === 'pending').length})
        </button>
        <button
          onClick={() => setFilter('in_progress')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'in_progress'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          En progreso ({orders.filter(o => o.status === 'in_progress').length})
        </button>
        <button
          onClick={() => setFilter('completed')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'completed'
              ? 'bg-indigo-600 text-white'
              : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-slate-600'
          }`}
        >
          Completados ({orders.filter(o => o.status === 'completed').length})
        </button>
      </div>

      {/* Lista de órdenes */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredOrders.map(order => (
          <OrderCard
            key={order.id}
            order={order}
            viewAs="client"
            onViewDetails={setSelectedOrder}
            onCancel={handleCancel}
            onDeleted={(orderId) => {
              setOrders((prev) => (prev || []).filter((o) => o.id !== orderId));
              if (selectedOrder?.id === orderId) setSelectedOrder(null);
            }}
          />
        ))}
      </div>

      {/* Botón cargar más */}
      {hasMore && (
        <div className="text-center mt-8">
          <Button
            variant="outline"
            onClick={loadMore}
            loading={loading}
          >
            Cargar más órdenes
          </Button>
        </div>
      )}

      {/* Modal de detalles */}
      {selectedOrder && (
        <OrderDetailsModal
          order={selectedOrder}
          viewAs="client"
          onClose={() => setSelectedOrder(null)}
          onAction={async (orderId) => {
            if (orderId) {
              const res = await getOrder(orderId);
              if (res.success && res.order) setSelectedOrder(res.order);
            } else {
              loadOrders();
            }
          }}
        />
      )}
    </div>
  );
};

export default PurchasesTab;
